import { useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { useSelector } from 'react-redux';

import Button, { Color, Size } from 'lib/Button';
import minusSvg from 'assets/icons/minus.svg';
import plusSvg from 'assets/icons/plus.svg';
import CopyToClipboardCellRenderer from 'components/common/CopyToClipboardCellRenderer';
import { getOOCFrameCountByLineId } from 'store/pages/programmaticOOC/selectors';
import { DealLineItemData } from 'components/common/types/DealItem';
import LinesExpandTreeIcon from './LinesExpandTreeIcon';
import ActionRenderer from '../ActionRenderer';

const SkeletonContent = ({ data }: { data: DealLineItemData }): React.ReactElement => (
  <>
    {LinesExpandTreeIcon(data.lineIndex === 0)}
    <div
      className="h-4 bg-gray-200 rounded w-48 ml-[20px] animate-pulse shrink-0"
      data-test-id={`skeleton-row-${data?.lineId}`}
    />
  </>
);

const OOCFramesLabel = ({ count }: { count: number }): React.ReactElement => (
  <span className="ml-2 text-xs text-gray-500 shrink-0 whitespace-nowrap">
    {count} OOC {count === 1 ? 'frame' : 'frames'}
  </span>
);

const LineContent = ({
  data,
  oocFrameCount,
}: {
  data: DealLineItemData;
  oocFrameCount: number | undefined;
}): React.ReactElement => {
  if (data?.isSkeletonRow) {
    return <SkeletonContent data={data} />;
  }

  if (data?.isChildRow) {
    return (
      <>
        {LinesExpandTreeIcon(data.lineIndex === 0)}
        <p className="ml-[20px] truncate">
          <CopyToClipboardCellRenderer value={data.dealName || 'Line'} />
        </p>
        {oocFrameCount != null && oocFrameCount > 0 && <OOCFramesLabel count={oocFrameCount} />}
      </>
    );
  }

  return (
    <p className="min-w-0 flex-1">
      <CopyToClipboardCellRenderer value={data?.dealName ?? ''} />
    </p>
  );
};

const LinesExpandRenderer = ({
  node,
  data,
  hasOutOfChargeEnabled,
}: ICellRendererParams & { hasOutOfChargeEnabled?: boolean }): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState(node.expanded);
  const isGroup = node.group || (data?.totalLineCount ?? 0) > 0;
  const oocFrameCountByLineId = useSelector(getOOCFrameCountByLineId);
  const oocFrameCount = hasOutOfChargeEnabled ? oocFrameCountByLineId[data.lineId] : undefined;

  const handleExpandClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    setIsExpanded(!node.expanded);
    node.setExpanded(!node.expanded);
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      {isGroup && !data?.isChildRow ? (
        <>
          <ActionRenderer data={data} />
          <Button
            classNames="ml-3 mr-1 -translate-x-[2px] shrink-0 !rounded-[50%] expand-button"
            onClick={handleExpandClick}
            svg={isExpanded ? minusSvg : plusSvg}
            color={Color.GREY}
            size={Size.SMALL}
            dataTestId={`expand-btn-${data?.dealId}`}
          />
        </>
      ) : (
        <span className="w-[50px] shrink-0" />
      )}
      <LineContent data={data} oocFrameCount={oocFrameCount} />
    </div>
  );
};

export default LinesExpandRenderer;
